import { ScopeOfPracticeEnum } from '@umss/shared/utils';
import { Location } from '../models/location.model';
// Shared authentication-related types for use across apps and libs

export type UserRole =
  | 'SuperAdmin'
  | 'BusinessAdministrator'
  | 'CorporateStaff'
  | 'FacilityAdmin'
  | 'ShiftAdministrator'
  | 'Contractor';

/**
 * User role constants to avoid magic strings
 * Use these in route guards and role checks
 */
export const UserRoles = {
  SuperAdmin: 'SuperAdmin' as UserRole,
  BusinessAdministrator: 'BusinessAdministrator' as UserRole,
  CorporateStaff: 'CorporateStaff' as UserRole,
  FacilityAdmin: 'FacilityAdmin' as UserRole,
  ShiftAdministrator: 'ShiftAdministrator' as UserRole,
  Contractor: 'Contractor' as UserRole
} as const;

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  emailVerified?: boolean;
  isActive?: boolean;
  lastLogin?: string;
  mustChangePassword?: boolean;
  phone?: string;
  fullName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ResetPasswordRequestDto {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface LoginRequestDto {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface ContractorRegisterRequestDto extends Omit<
  RegisterRequestDto,
  'role'
> {
  scopeOfPractice: ScopeOfPracticeEnum;
  licenseNumber?: string;
  licenseRenewalDate?: string;
  maxDriveTimeMinutes?: number;
  bio?: string;
  yearsExperience?: number;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateCode?: string;
  zipCode?: string;
}

export interface RegisterRequestDto {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
}

export interface FacilityAdminRegisterRequestDto extends Omit<
  RegisterRequestDto,
  'role'
> {
  positionName: string;
  facilityName: string;
  facilityContactEmail: string;
  facilityContactPhone: string;
  facilityTypeId: number;
  locationId?: number;
  location?: Location;
  facilityDescription?: string;
  facilityWebsiteUrl?: string;
}

export interface CorporateAdminRegisterRequestDto extends Omit<
  RegisterRequestDto,
  'role'
> {
  positionName: string;
  corporationName: string;
  corporationContactEmail: string;
  corporationContactPhone: string;
  locationId?: number;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  corporationDescription?: string;
  corporationWebsiteUrl?: string;
}

export interface SocialLoginRequestDto {
  provider: SocialLoginProvider;
  accessToken: string;
  role?: UserRole;
}

export enum SocialLoginProvider {
  Facebook = 'facebook',
  LinkedIn = 'linkedin'
}

export interface RefreshTokenRequestDto {
  refreshToken: string;
}

export interface FacilityAccessDto {
  facilityId: string;
  facilityName: string;
  permissions: string[];
}

export interface AuthenticationResponseDto {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: UserDto;
  permissions?: string[];
  facilityAccess?: FacilityAccessDto;
  contractorId?: string;
  corporateAccess?: CorporateAccessDto[];
}

export interface UserAuthenticationResponseDto {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  message: string;
  userId: string;
  email: string;
}

export interface FacilityAdminAuthenticationResponseDto extends UserAuthenticationResponseDto {
  facilityId: string;
  facilityName: string;
  onboardingToken: string;
  onboardingTokenExpiresAt: string;
}

export interface ContractorAuthenticationResponseDto extends UserAuthenticationResponseDto {
  contractorId: string;
}

export interface CorporateAdminAuthenticationResponseDto extends UserAuthenticationResponseDto {
  corporationId: string;
  corporationName: string;
  corporateAdminId: string;
  corporateRoleId: string;
  onboardingToken: string;
  onboardingTokenExpiresAt: string;
  permissions: string[];
  corporateAccess: CorporateAccessDto[];
}
export interface CorporateAccessDto {
  corporationId: string;
  corporationName: string;
  permissions: string[];
}
export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  status?: number;
}

export interface AuthState {
  lastSessionAt: string | null;
  user: UserDto | null;
  permissions: string[];
  role: UserRole | null;
  facilityIds: string[];
  contractorId: string | null;
  expiresAt: string | null;
  corporateId?: string | null;
}

export interface FacebookAuth {
  status: string;
  authResponse: FacebookAuthResponse;
}

export interface FacebookAuthResponse {
  userID: string;
  expiresIn: number;
  accessToken: string;
  signedRequest: string;
  graphDomain: string;
  grantedScopes: string;
  data_access_expiration_time: number;
}

export interface IssueDelegatedLoginResponseDto {
  redirectUrl?: string;
}
